<template>
  <div class="employees-tab">
    <div class="employees-tab__header">
      <div class='employees-tab__header__top'>
        <div class='employees-tab__header__top__filter'>
          <filtered-search
            class='search-filter'
            @searchInput="searchChange"
            @filtersInput="filtersChange"
            :show-filters="true"
            :filters="filters"
            tab="employees"
          />
          <div class="employees-tab__header__top__filter__switch">
            <v-switch
              v-model="hasCredentials"
              :label="$t('configuration.has.credentials')"
            ></v-switch>
          </div>
          <div class="employees-tab__header__top__filter__switch">
            <v-switch
              v-model="authorized"
              :label="$t('configuration.employees.AUTHORIZED')"
            ></v-switch>
          </div>
        </div>
        <div class='employees-tab__header__top__initiate'>
          <custom-tooltip placement="right" popper-class="light-tooltip">
            <div slot='buttonContent'>
              <v-btn
                small
                class="initiate-btn"
                @click='openPopup'
                :disabled='!isAllSelected && !selectedEmployees.length'
              >
                {{ $t('initiate.password.spray.scan') }}
              </v-btn>
            </div>
            <div slot="content">
              <slot name="tooltip">
                {{ toolTipText }}
              </slot>
            </div>
          </custom-tooltip>
          <download-c-s-v class="download-csv-btn" :class="{'disabled': dataLoading || employeesLoading}" @click="downloadCSVReport"/>
        </div>
      </div>
      <div class='employees-tab__header__bottom'>
        <div class="select-all">
          <CustomCheckbox
            :color="isIndeterminate ? 'pink' : 'cold-marine'"
            :value="employeesCount > 0"
            :indeterminate="isIndeterminate"
            size="s"
            @input="selectAll"
          />
          <div class="select-all__label">
            {{ `${employeesCount} ${selectAllLabel}` }}
          </div>
        </div>
      </div>
    </div>
    <div class='content' v-if='!dataLoading && !employeesLoading'>
      <no-data
        v-if='!employeesPaginatedData.length'
        :message='messageForNoData'
      >
        <div slot="action">
          <div class='no-data no-results' v-if='hasFilter && tldsWithEmployeesScan.length'>
            <div class='title'>
              {{ noDataMessageTitle }}
            </div>
            <div class='domains'>
              {{ noDataMessageDomains }}
            </div>
            <v-divider v-if='tldsWithNoEmployeesScan.length' class='divider'/>
          </div>
          <div class='initiate-test' v-if='hasFilter && tldsWithNoEmployeesScan.length'>
            <div class='no-data'>
              <div class='title'>
                {{ noScansMessageTitle }}
              </div>
              <div class='domains'>
                {{ noScansMessageDomains }}
              </div>
            </div>
            <custom-tooltip placement="right" popper-class="light-tooltip">
              <div slot='buttonContent'>
                <v-btn class="initiate-test-btn" @click="initiateLeaksTest" :disabled='initiateTestClicked'>
                  {{ $t('configuration.initiate.test') }}
                </v-btn>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ toolTipText }}
                </slot>
              </div>
            </custom-tooltip>
            <span class="scan-initiated" v-if='testInitiated'>
                {{ scanInitiated }}
              </span>
          </div>
        </div>
      </no-data>
      <div v-else>
        <wombat-table
          class="scan-data-table"
          :name="employeesName"
          :items="employeesPaginatedData"
          :columns="employeesTableColumns"
          :resize="false"
          :loadingItems="true"
          :columnsReorder="false"
          :itemHeight="58"
        >
          <component
            slot="row-cell"
            slot-scope="rowCell"
            class="row-cell"
            :is="tableRowCellComponentsHashEmployees[rowCell.column.name] || tableRowCellComponentsHashEmployees.default"
            :item="rowCell.item"
            :column="rowCell.column"
            :selected-employees='selectedEmployees'
            :excluded-employees="excludedEmployees"
            :is-all-selected="isAllSelected"
            @checked='checked'
            @leakSourceHelp="helpClicked"
            @showCredentials="showDataClicked"
          />
        </wombat-table>
        <div class="employees-tab__footer">
          <advanced-pagination
            tab='employees'
            :page="page"
            :pages-amount='pages'
            :is-show-pagination='isShowPagination'
            @changedSelectedPage='changedSelectedPage'
            @changedItemsAmountPerPage='changedItemsAmountPerPage'
          />
        </div>
      </div>
    </div>
    <div class='employees-tab-loading' v-else />
    <page-loading-overlay
      :show="employeesLoading && currentTab === 'employees'"
      :progress="employeesProgress"
      contained
    />
    <initiate-password-spray-scan-popup
      :visible-popup='showPopup'
      :selectedEmployees="selectedFullEmployees"
      :excludedEmployees="excludedEmployees"
      :allSelected="isAllSelected"
      @save='initiatePassSprayScan'
      @cancel='showPopup = false'
    />
    <password-spray-scan-initiated-popup
      :visible-popup="showScanInitiated"
      @save="showScanInitiated = false"
    />
    <yes-no-popup
      title='no.selected.employees.popup.title'
      :visible-popup='showNoSelectedEmployeesPopup'
      :showNoButton='false'
      yes-caption='no.selected.employees.popup.ok'
      message='no.selected.employees.popup.message'
      @clicked='showNoSelectedEmployeesPopup = false'
    />
    <leak-source-help-popup
      :visible-popup='showHelpPopup'
      :data = 'showHelpPopupData'
      @clicked='closeHelpPopup'
    />
    <leak-data-popup
      :visible-popup='showDataPopup'
      :data = 'showDataPopupData'
      @clicked='closeDataPopup'
    />
    <success-operation-message
      :message='successMessage'
      @close="clearSuccessMessage"
    />
    <error-message :errorText="errorMessage" @close="errorMessage = ''" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import { employeesColumns } from '@/services/tableColumns';
import LeakDataTableCell from '@/components/tableCells/employeesTableCells/LeakDataTableCell.vue';
import EmailDataTableCell from '@/components/tableCells/employeesTableCells/EmailDataTableCell.vue';
import NameDataTableCell from '@/components/tableCells/employeesTableCells/NameDataTableCell.vue';
import AuthorizedTableCell from '@/components/tableCells/employeesTableCells/AuthorizedTableCell.vue';
import LeakSourceTableCell from '@/components/tableCells/employeesTableCells/LeakSourceTableCell.vue';
import SuccessOperationMessage from '@/components/common/SuccessOperationMessage.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import {
  ADD_NEW_TEST_PROJECT,
  INITIATE_PASSWORD_SPRAY_SCAN,
  INITIATE_TEST_CARD,
} from '@/store/project/actionTypes';
import InitiatePasswordSprayScanPopup from '@/components/popup/InitiatePasswordSprayScanPopup.vue';
import PasswordSprayScanInitiatedPopup from '@/components/popup/PasswordSprayScanInitiatedPopup.vue';
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import FilteredSearch from '@/components/common/FilteredSearch.vue';
import SelectedEmployeeTableCell from '@/components/tableCells/employeesTableCells/SelectedEmployeeTableCell.vue';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import { UPDATE_ITEMS_PER_PAGE } from '@/store/pagination/actionTypes';
import AdvancedPagination from '@/components/common/AdvancedPagination.vue';
import moment from 'moment';
import { TEST_REPEAT_STATUS, TEST_TYPES_TLDS } from '@/constants/projects';
import { getGroupedTimezonesList } from '@/utils/dateUtils';
import { CONFIGURATION_ACTION_TYPES } from '@/constants/configuration';
import LeakSourceHelpPopup from '@/components/popup/LeakSourceHelpPopup.vue';
import LeakDataPopup from '@/components/popup/LeakDataPopup.vue';
import DownloadCSV from '@/assets/icons/download_csv.svg?inline';
import LeakDateTableCell from '@/components/tableCells/employeesTableCells/LeakDateTableCell.vue';
import UrlTableCell from '@/components/tableCells/employeesTableCells/UrlTableCell.vue';
import SoftwareTableCell from '@/components/tableCells/employeesTableCells/SoftwareTableCell.vue';
import {
  CHANGE_EMPLOYEES_LIST_OPTIONS,
  LOAD_PASSWORD_SPRAY_STATS_UPDATED,
  RESET_SUCCESS_OPERATION_MESSAGE, UPDATE_SELECTED_EMPLOYEES,
} from '@/store/configuration/actionTypes';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import NoData from '@/components/common/NoData.vue';
import {downloadCSVReportByTab} from '@/services/repository';

const getTimeWithHourOffset = offset => moment()
  .startOf('hour')
  .add(offset, 'hour')
  .format('HH:mm');

const getTimeWithMinuteOffset = offset => moment()
  .startOf('minute')
  .add(offset, 'minute')
  .format('HH:mm');

export default {
  name: 'EmployeesTab',
  components: {
    NoData,
    LeakDataPopup,
    LeakSourceHelpPopup,
    AdvancedPagination,
    YesNoPopup,
    PageLoadingOverlay,
    FilteredSearch,
    InitiatePasswordSprayScanPopup,
    PasswordSprayScanInitiatedPopup,
    WombatTable,
    WombatRowDefaultCell,
    CustomCheckbox,
    DownloadCSV,
    SuccessOperationMessage,
    ErrorMessage,
    CustomTooltip,
  },
  props: {
    currentTab: {
      required: false,
    },
    hasFilter: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showScanInitiated: false,
      showNoSelectedEmployeesPopup: false,
      selectedEmployees: [],
      excludedEmployees: [],
      isAllSelected: false,
      showPopup: false,
      showHelpPopup: false,
      showDataPopup: false,
      showHelpPopupData: [],
      showDataPopupData: [],
      hasCredentials: false,
      authorized: false,
      page: 1,
      initiateTestClicked: false,
      testInitiated: false,
      pollingPasswordSprayStats: null,
      pollingEmployeesData: null,
      pollingPasswordSprayScan: null,
      filters: [
        {name: 'Name', attr: 'name'},
        {name: 'Email', attr: 'email'},
        {name: 'Leak Date', attr: 'leakDate'},
        {name: 'Leak Source', attr: 'leakSource'},
        {name: 'Credentials', attr: 'credentials'},
      ],
      search: '',
      filterSearch: [],
      successMessage: '',
      approveLoadingOnSearchChange: false,
      errorMessage: '',
      filtersForPayload: {
        search: '',
        filter: [],
        toggles: {
          hasCredentials: false,
          authorized: false,
        },
      },
      firstTimeCalled: false,
    };
  },
  async beforeDestroy() {
    await this.resetIntervals();
  },
  watch: {
    successOperationMessage() {
      this.successMessage = this.successOperationMessage;
    },
    async currentTab() {
      if (this.currentTab === 'employees' && this.domainFilter.length && !this.firstTimeCalled) {
        this.firstTimeCalled = true;
        await this.loadEmployees();
        await this.$store.dispatch(LOAD_PASSWORD_SPRAY_STATS_UPDATED, {domain: this.domainFilter});
        await this.pollPasswordSprayStats();
      }
      this.resetSelectedEmployees();
    },
    async domainFilter() {
      this.resetSelectedEmployees();
      if (this.currentTab !== 'employees' && this.domainFilter.length) {
        this.firstTimeCalled = false;
      }
      if (this.currentTab === 'employees' && this.domainFilter.length) {
        this.approveLoadingOnSearchChange = false;
        this.page = 1;
        this.search = '';
        await this.$store.dispatch(LOAD_PASSWORD_SPRAY_STATS_UPDATED, {domain: this.domainFilter});
        await this.resetIntervals();
        await this.pollPasswordSprayStats();
        await this.loadEmployees();
      }
    },
    async hasCredentials() {
      this.page = 1;
      await this.loadEmployees();
    },
    async authorized() {
      this.page = 1;
      await this.loadEmployees();
    },
    excludedEmployees() {
      if (this.excludedEmployees.length === this.filtersForPayload.total && this.isAllSelected) {
        this.isAllSelected = false;
      }
    },
    selectedEmployees() {
      if (this.selectedEmployees.length === this.filtersForPayload.total && !this.isAllSelected) {
        this.isAllSelected = true;
      }
    },
    async isAllSelected() {
      if (!this.isAllSelected) {
        this.excludedEmployees = [];
        this.resetFiltersForPayload();
      }
      if (this.isAllSelected) {
        const items = [];
        this.employeesItems.forEach(item => {
          items.push({ ...item, selected: true });
        });
        await this.$store.dispatch(UPDATE_SELECTED_EMPLOYEES, items);
        this.selectedEmployees = [];
        this.resetFiltersForPayload();
      }
    },
  },
  computed: {
    ...mapGetters([
      'paginationItemsPerPageByTabs',
      'dataLoading',
      'employeesLoading',
      'domainFilter',
      'successOperationMessage',
      'amountOfRunningProjects',
      'licenses',
      'configurationDomains',
      'companyData',
      'organizationFilter',
      'employeesPaginationState',
      'employeesItems',
      'tldsWithNoEmployeesScan',
      'tldsWithEmployeesScan',
      'passwordSprayStatsUpdated',
      'employeesRefId',
      'passwordSprayRefId',
      'passwordSprayIntervalRunning',
      'passwordSprayInitiatedDomains',
      'employeesRunningInterval',
      'employeesProgress',
    ]),
    toolTipText() {
      return this.$t('configuration.add.test');
    },
    formattedTimeZone() {
      return getGroupedTimezonesList().map(tz => ({
        value: tz.offset,
        name: tz.name,
        id: tz.id,
      }));
    },
    selectedTimeZone() {
      return this.formattedTimeZone.find(timezone => timezone.value === String(moment().utcOffset()));
    },
    messageForNoData() {
      return !this.hasFilter
        ? this.$t('no.filter.infrastructure.no.data.message')
        : '';
    },
    allTldsHasEmployeesScan() {
      return !this.tldsWithNoEmployeesScan.length;
    },
    noDataMessage() {
      return this.allTldsHasEmployeesScan ? this.$t('no.results.found') : '';
    },
    noScansMessageTitle() {
      return this.$t('no.employees.scans.found.on');
    },
    noDataMessageTitle() {
      return this.$t('no.results.found');
    },
    noScansMessageDomains() {
      return this.tldsWithNoEmployeesScan.length ? `${this.tldsWithNoEmployeesScan.join(', ')}` : '';
    },
    scanInitiated() {
      return this.$t('test.initiated.successfully');
    },
    noDataMessageDomains() {
      return `${this.tldsWithEmployeesScan?.length ? this.tldsWithEmployeesScan.join(', ') : ''}`;
    },
    itemsPerPage() {
      return this.paginationItemsPerPageByTabs.employees;
    },
    selectAllLabel() {
      return this.$t('configuration.infrastructure.assets.selected.employees');
    },
    employeesCount() {
      if (this.isAllSelected) {
        return (this.filtersForPayload.total - this.excludedEmployees.length + this.selectedEmployees.length);
      }
      return this.selectedEmployees.length || 0;
    },
    isIndeterminate() {
      return this.selectedEmployees.length !== this.employeesPaginationState.total && !this.isAllSelected;
    },
    employeesTableColumns() {
      const columns = [
        employeesColumns.selected,
        employeesColumns.employeeName,
        employeesColumns.employeeEmail,
        employeesColumns.employeeLeakDate,
        employeesColumns.employeeLeakSource,
        employeesColumns.employeePassword,
        employeesColumns.employeeUrl,
        employeesColumns.employeeSoftware,
        employeesColumns.authorized,
      ].filter(column => column)
        .map(column => ({
          ...column,
          title: column.title && this.$t((`configuration.employees.${column.title}`).toString()),
        }));
      return [...columns];
    },
    tableRowCellComponentsHashEmployees() {
      return {
        selected: SelectedEmployeeTableCell,
        name: NameDataTableCell,
        email: EmailDataTableCell,
        leakDate: LeakDateTableCell,
        leakSource: LeakSourceTableCell,
        password: LeakDataTableCell,
        url: UrlTableCell,
        software: SoftwareTableCell,
        authorized: AuthorizedTableCell,
        default: WombatRowDefaultCell,
      };
    },
    employeesName() {
      return 'employees-table';
    },
    employeesPaginatedData() {
      return this.employeesItems.map((item, index) => ({
        ...item,
        id: index + 1,
      }));
    },
    pages() {
      return this.employeesPaginationState?.pageCount || Math.ceil(this.employeesItems.length / this.itemsPerPage);
    },
    isShowPagination() {
      return this.pages > 1;
    },
    selectedFullEmployees() {
      return this.employeesPaginatedData.filter(employee => this.selectedEmployees.includes(employee.email) && employee.password.length && employee.password[0].length >= 1);
    },
  },
  methods: {
    async loadEmployees(turnOnLoading = true) {
      if (this.currentTab === 'employees') {
        clearInterval(this.pollingEmployeesData);
        await this.$store.dispatch(CHANGE_EMPLOYEES_LIST_OPTIONS, {
          sort: {
            default: 'asc',
          },
          limit: this.itemsPerPage,
          page: this.page,
          search: this.search,
          filter: this.filterSearch,
          hasCredentials: this.hasCredentials,
          authorized: this.authorized,
          domain: this.domainFilter || [],
          organizations: this.organizationFilter || [],
          turnOnLoading,
          tab: 'employees',
          selectAllFilters: {
            ...this.filtersForPayload, selected: this.selectedEmployees, excluded: this.excludedEmployees, isAllSelected: this.isAllSelected,
          },
        });
        if (this.employeesRefId && this.employeesRefId !== '') {
          this.pollingEmployeesData = setInterval(async () => {
            if (!this.employeesRefId || this.employeesRefId === '') {
              clearInterval(this.pollingEmployeesData);
            } else if (!this.employeesRunningInterval) {
              await this.$store.dispatch(CHANGE_EMPLOYEES_LIST_OPTIONS, {
                sort: {
                  default: 'asc',
                },
                limit: this.itemsPerPage,
                page: this.page,
                search: this.search,
                filter: this.filterSearch,
                hasCredentials: this.hasCredentials,
                authorized: this.authorized,
                domain: this.domainFilter || [],
                organizations: this.organizationFilter || [],
                turnOnLoading,
                tab: 'employees',
                refId: this.employeesRefId,
                selectAllFilters: {
                  ...this.filtersForPayload, selected: this.selectedEmployees, excluded: this.excludedEmployees, isAllSelected: this.isAllSelected,
                },
              });
            }
          }, 3000);
        }
      }
    },
    resetSelectedEmployees() {
      this.selectedEmployees = [];
      this.excludedEmployees = [];
      this.isAllSelected = false;
      this.filtersForPayload = {
        search: '',
        filter: [],
        toggles: {
          hasCredentials: false,
          authorized: false,
        },
      };
    },
    resetFiltersForPayload() {
      this.filtersForPayload = {
        search: this.search,
        filter: this.filterSearch,
        toggles: {
          hasCredentials: this.hasCredentials,
          authorized: this.authorized,
        },
        total: this.employeesPaginationState.total,
      };
    },
    resetIntervals() {
      clearInterval(this.pollingPasswordSprayStats);
      clearInterval(this.pollingEmployeesData);
      clearInterval(this.pollingPasswordSprayScan);
    },
    async searchChange(e) {
      this.search = e.trim();
      this.search = this.extractSearchString();
      this.page = 1;
      if (this.approveLoadingOnSearchChange || this.search.length) {
        await this.loadEmployees();
      }
      if (!this.approveLoadingOnSearchChange) {
        this.approveLoadingOnSearchChange = true;
      }
    },
    async filtersChange(e) {
      this.filterSearch = e;
      this.filterSearch = this.extractFilters();
      this.page = 1;
      await this.loadEmployees();
    },
    extractSearchString() {
      if (!this.search) {
        return '';
      }
      let results = this.search.split(' ');
      results = results.filter(result => !result.includes(':'));
      return results.join(' ').trim();
    },
    extractFilters() {
      const filtersMap = new Map();
      if (this.search) {
        let results = this.search.split(' ');
        results = results.filter(result => result.includes(':'));
        results.forEach(item => {
          const filter = item.split(':');
          if (filter.length > 1 && filter[1].length > 0) {
            filtersMap.set(filter[0], filter[1].split(',').map(element => element.trim()));
          }
        });
      }
      if (this.filterSearch.length > 0) {
        this.filterSearch.forEach(item => {
          const filter = item.split(':');
          if (filtersMap.has(filter[0])) {
            const filtersValues = filtersMap.get(filter[0]);
            filter[1].split(',').forEach(value => {
              filtersValues.push(value.trim());
            });
            filtersMap.set(filter[0], filtersValues);
          } else {
            const filtersValues = [];
            filter[1].split(',').forEach(value => {
              filtersValues.push(value.trim());
            });
            filtersMap.set(filter[0], filtersValues);
          }
        });
      }
      return filtersMap;
    },
    async downloadCSVReport() {
      const payload = {
        sort: {
          default: 'asc',
        },
        search: this.search,
        filter: this.filterSearch,
        toggles: {
          hasCredentials: this.hasCredentials,
          authorized: this.authorized,
        },
        domain: this.domainFilter || [],
        organizations: this.organizationFilter || [],
        tab: 'employees',
      };
      const dataStream = await downloadCSVReportByTab(payload);
      const blob = new Blob([dataStream], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `${Date.now()}-employees.csv`);
      document.body.appendChild(link);
      link.click();
    },
    async initiateLeaksTest() {
      this.initiateTestClicked = true;
      for (let i = 0; i < this.tldsWithNoEmployeesScan.length; i += 1) {
        try {
          const test = {
            url: this.tldsWithNoEmployeesScan[i],
            tld: this.tldsWithNoEmployeesScan[i],
            type: TEST_TYPES_TLDS.employees,
            schedule: false,
            isAllDay: false,
            startDate: moment().format(),
            startTime: getTimeWithMinuteOffset(2),
            endTime: getTimeWithHourOffset(2),
            timezone: this.selectedTimeZone.id,
            customRepeatData: {},
            frequency: TEST_REPEAT_STATUS.never,
          };
          // eslint-disable-next-line no-await-in-loop
          const createdTest = await this.$store.dispatch(ADD_NEW_TEST_PROJECT, test);
          if (createdTest) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await this.$store.dispatch(INITIATE_TEST_CARD, [createdTest.id]);
            } catch (e) {
              this.errorMessage = e.message;
            }
          }
        } catch (e) {
          this.errorMessage = e.message;
        }
      }
      this.testInitiated = true;
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      window.scrollTo(0, 0);
      this.page = 1;
      await this.$store.dispatch(UPDATE_ITEMS_PER_PAGE, { itemsPerPage, tab: 'employees'});
      await this.loadEmployees();
    },
    async changedSelectedPage(currentPage) {
      window.scrollTo(0, 0);
      this.page = currentPage;
      await this.loadEmployees();
    },
    selectAll() {
      this.isAllSelected = !this.isAllSelected;
      if (this.selectedEmployees.length) {
        this.selectedEmployees = [];
      }
      if (this.excludedEmployees.length) {
        this.excludedEmployees = [];
      }
      this.resetFiltersForPayload();
    },
    helpClicked(data) {
      this.showHelpPopup = true;
      this.showHelpPopupData = data;
    },
    showDataClicked(data) {
      this.showDataPopup = true;
      this.showDataPopupData = data;
    },
    closeHelpPopup() {
      this.showHelpPopup = false;
      this.showHelpPopupData = [];
    },
    closeDataPopup() {
      this.showDataPopup = false;
      this.showDataPopupData = [];
    },
    checked(item) {
      if (this.isAllSelected) {
        if (item?.selected !== undefined) {
          if (item.selected) {
            const index = this.excludedEmployees.findIndex(employee => employee === item.email);
            if (index !== -1) {
              this.excludedEmployees.splice(index, 1);
            } else {
              this.excludedEmployees.push(item.email);
            }
          } else {
            const index = this.selectedEmployees.findIndex(employee => employee === item.email);
            if (index !== -1) {
              this.selectedEmployees.splice(index, 1);
            } else {
              this.selectedEmployees.push(item.email);
            }
            if (this.excludedEmployees.includes(item.email)) {
              const excludeIndex = this.excludedEmployees.findIndex(data => data === item.email);
              this.excludedEmployees.splice(excludeIndex, 1);
              this.filtersForPayload.total -= 1;
            }
          }
        } else {
          const index = this.excludedEmployees.findIndex(employee => employee === item.email);
          if (index !== -1) {
            this.excludedEmployees.splice(index, 1);
          } else {
            this.excludedEmployees.push(item.email);
          }
        }
      } else {
        const index = this.selectedEmployees.findIndex(employee => employee === item.email);
        if (index !== -1) {
          this.selectedEmployees.splice(index, 1);
        } else {
          this.selectedEmployees.push(item.email);
        }
      }
    },
    openPopup() {
      if (this.selectedFullEmployees.length === 0 && !this.isAllSelected) {
        this.showNoSelectedEmployeesPopup = true;
      } else {
        this.showPopup = true;
      }
    },
    async pollPasswordSprayStats() {
      this.pollingPasswordSprayStats = setInterval(async () => {
        if (this.domainFilter.length && this.employeesRefId === '') {
          await this.$store.dispatch(LOAD_PASSWORD_SPRAY_STATS_UPDATED, {domain: this.domainFilter});
          if (this.passwordSprayStatsUpdated) {
            await this.loadEmployees(false);
          }
        }
      }, 40000);
    },
    async initiatePassSprayScan(corpDomain = '') {
      if (this.currentTab === 'employees') {
        clearInterval(this.pollingPasswordSprayScan);
        const payload = {
          search: this.filtersForPayload?.search || '',
          filter: Object.keys(this?.filtersForPayload)?.length ? JSON.stringify(Object.fromEntries(this.filtersForPayload.filter)) : {},
          toggles: {
            hasCredentials: this.filtersForPayload?.toggles?.hasCredentials || false,
            authorized: this.filtersForPayload?.toggles?.authorized || false,
          },
          isAllSelected: this.isAllSelected,
          domain: this.domainFilter || [],
          organizations: this.organizationFilter || [],
          tab: 'employees',
          selected: this.selectedEmployees,
          excluded: this.excludedEmployees,
          corp_domain: corpDomain,
          timezone: this.selectedTimeZone.id,
          successMessageType: CONFIGURATION_ACTION_TYPES.PASSWORD_SPRAY,
        };
        await this.$store.dispatch(INITIATE_PASSWORD_SPRAY_SCAN, payload);
        this.showPopup = false;
        this.showScanInitiated = true;
        await this.$store.dispatch(LOAD_PASSWORD_SPRAY_STATS_UPDATED, {domain: this.domainFilter});
        if (this.passwordSprayStatsUpdated) {
          this.page = 1;
          await this.loadEmployees(false);
        }
        this.resetSelectedEmployees();
      }
    },
    async clearSuccessMessage() {
      await this.$store.dispatch(RESET_SUCCESS_OPERATION_MESSAGE);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.employees-tab {
  padding: 0 $space-8 $space-4 $space-8;
  margin-top: rem(-13);
  position: relative; // Required for contained loading overlay
  min-height: calc(100vh - 200px); // Ensure overlay has space to display

  .initiate-test-btn.initiate-test-btn {
    width: fit-content;
    padding: $space-3 $space-6;
    background-color: transparent !important;
    border: 1px solid var(--border-primary) !important;
    border-radius: $radius-sm;
    box-shadow: $shadow-sm;
    color: var(--text-primary) !important;
    font-family: $default-application-font-medium;
    font-size: $font-size-sm;
    font-weight: 500;
    letter-spacing: 1.1px;
    line-height: $font-size-md;
    text-align: center;
    margin-bottom: $space-2;
    text-transform: uppercase;
    &:hover {
      background-color: var(--bg-tertiary);
      border-color: var(--border-secondary);
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    margin-bottom: $space-5;

    &__top {
      display: flex;
      justify-content: space-between;
      margin-bottom: $space-5;

      &__filter {
        display: flex;

        &__switch {
          padding-left: $space-12;
          margin-top: $space-2;
        }
      }

      &__initiate {
        display: flex;

        .download-csv-btn {
          margin-top: 15%;
          color: var(--text-inverse);
          font-family: $default-application-font-medium;
          font-size: $font-size-sm;
          height: $btn-height-md;
          letter-spacing: 1.1px;
          line-height: $font-size-md;
          width: rem(78);

          &:hover {
            cursor: pointer;
          }

          &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        }

        .initiate-btn {
          margin-top: 15%;
          font-family: $default-application-font-medium;
          font-size: $font-size-sm;
          height: $btn-height-sm;
          border-radius: $radius-sm;
          background-color: var(--text-secondary) !important;
          color: var(--text-inverse) !important;
          box-shadow: $shadow-sm;
        }
      }
    }

    &__bottom {
      .select-all {
        display: flex;
      }
    }
  }

  &__footer {
    padding-top: $space-5;
  }

  .employees-tab-loading {
    align-self: center;
    justify-self: center;
    margin-top: 20%;
  }

  .initiate-test {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-items: center;

    .scan-initiated {
      color: var(--brand-primary);
      font-family: $default-application-font-semi-bold;
      font-size: $font-size-sm;
      text-transform: uppercase;
    }
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.employees-tab {
  .wombat-table .ps__rail-x {
    display: none !important;
  }

  .no-data {
    &.no-results {
      align-items: center;
      margin-bottom: $space-2;

      .domains {
        margin-bottom: $space-1;
        font-size: $font-size-base;
        text-align: left;
      }

      .divider {
        width: 100%;
      }
    }
  }

  .initiate-test {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .no-data {
      .domains {
        margin-bottom: $space-1;
        font-size: $font-size-base;
        text-align: left;
      }
    }
  }

  .no-result-found {
    max-width: 40%;
    text-align: center;
    font-size: $font-size-lg;
    margin-top: 7%;
    display: flex;
    flex-direction: column;
    justify-items: center;
    color: var(--text-muted);
    opacity: 1;

    .no-scans {
      margin-bottom: $space-8;

      .title {
        display: flex;
        justify-content: center;
        font-size: $font-size-4xl;
        margin: auto;
        overflow: hidden;
        text-transform: capitalize;
        opacity: 0.6;
      }

      .domains {
        text-align: left;
        margin-bottom: $space-4;
        opacity: 0.6;
      }
    }
  }
}

.employees-tab__header {
  margin-top: $space-1;

  .search-filter {
    box-shadow: none;
    border: 1px solid var(--border-primary);
    border-radius: $radius-sm;
    margin-top: $space-4;
  }
}

.employees-tab .initiate-btn.v-btn.v-btn:not(.v-btn--disabled) {
  color: var(--text-inverse);
}
</style>
